import LoadingScreen from '../components/LoadingScreen';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAppContext } from '../context/AppContext';

const CITIES = ['Казань', 'Зеленодольск', 'Волжск'];
const DISTRICTS = ['Город', 'Мирный', 'Зеленый дол'];
const DISTRICT_SHORT = { 'Город': 'Город', 'Мирный': 'Мирный', 'Зеленый дол': 'Зел.Дол' };

const formatDeliveryDate = (day) => {
  if (!day) return '—';
  const parts = day.split('-');
  if (parts.length !== 3) return day;
  const date = new Date(day); date.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate()+1);
  const short = `${parts[2]}.${parts[1]}`;
  if (date.getTime() === today.getTime()) return `Сегодня · ${short}`;
  if (date.getTime() === tomorrow.getTime()) return `Завтра · ${short}`;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const shortAddress = (address) => {
  if (!address) return '—';
  return address.split(',').map(s => s.trim()).slice(0, 2).join(', ');
};

export default function AdminOrdersScreen() {
  const navigate = useNavigate();
  const { user } = useAppContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState(null);
  const [districtFilter, setDistrictFilter] = useState(null);
  const [takingId, setTakingId] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      const data = await api.orders.adminAll();
      const sorted = (data || []).slice().sort((a, b) => {
        const da = a.delivery_day || '', db = b.delivery_day || '';
        if (da !== db) return da.localeCompare(db);
        return new Date(a.created_at) - new Date(b.created_at);
      });
      setOrders(sorted);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.isAdmin) loadOrders(); else setLoading(false); }, [user, loadOrders]);

  const handleTake = async (orderId, e) => {
    e.stopPropagation();
    setTakingId(orderId);
    try {
      const updated = await api.orders.take(orderId);
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o));
    } catch { alert('Не удалось взять заказ'); }
    finally { setTakingId(null); }
  };

  const myOrders = orders.filter(o => String(o.courier_id) === String(user?.id));
  const availableOrders = orders.filter(o => !o.courier_id);
  const filteredOrders = availableOrders.filter(o => {
    if (cityFilter && o.city !== cityFilter) return false;
    if (cityFilter === 'Зеленодольск' && districtFilter && o.district !== districtFilter) return false;
    return true;
  });
  const cityCount = (city) => availableOrders.filter(o => o.city === city).length;
  const districtCount = (d) => availableOrders.filter(o => o.city === 'Зеленодольск' && o.district === d).length;

  if (!user?.isAdmin) return <div style={centered}><p>Доступ ограничен</p></div>;
  if (loading) return <LoadingScreen />;

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', marginTop: 16, marginBottom: 12 }}>Все заказы</h1>

      {/* Табы городов */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 4 }}>
        <Tab active={cityFilter === null} onClick={() => { setCityFilter(null); setDistrictFilter(null); }} label="ВСЕ" count={availableOrders.length} />
        {CITIES.map(city => {
          const count = cityCount(city);
          if (!count) return null;
          return <Tab key={city} active={cityFilter === city} onClick={() => { setCityFilter(city); setDistrictFilter(null); }} label={city} count={count} />;
        })}
      </div>

      {/* Табы районов */}
      {cityFilter === 'Зеленодольск' && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 4 }}>
          <Tab active={districtFilter === null} onClick={() => setDistrictFilter(null)} label="Все" count={cityCount('Зеленодольск')} sub />
          {DISTRICTS.map(d => {
            const count = districtCount(d);
            if (!count) return null;
            return <Tab key={d} active={districtFilter === d} onClick={() => setDistrictFilter(d)} label={DISTRICT_SHORT[d]} count={count} sub />;
          })}
        </div>
      )}

      {/* В работе */}
      {myOrders.length > 0 && (
        <div onClick={() => navigate('/admin-my-work')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#4CAF5018', borderRadius: 14, border: '1.5px solid #4CAF50', padding: 16, marginBottom: 8, cursor: 'pointer' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#4CAF50' }}>В работе</div>
            <div style={{ fontSize: 13, color: '#4CAF50', opacity: 0.8, marginTop: 2 }}>
              {myOrders.length} {myOrders.length === 1 ? 'заказ' : myOrders.length < 5 ? 'заказа' : 'заказов'} · нажмите чтобы открыть
            </div>
          </div>
          <span style={{ fontSize: 20, color: '#4CAF50', fontWeight: 700 }}>→</span>
        </div>
      )}

      {filteredOrders.length === 0 ? (
        <div style={{ background: 'var(--card)', borderRadius: 14, padding: 24, textAlign: 'center' }}>
          <p style={{ fontWeight: 700 }}>{availableOrders.length === 0 ? 'Активных заказов нет' : 'Нет заказов по фильтру'}</p>
        </div>
      ) : filteredOrders.map(order => (
        <div key={order.id} style={{ background: 'var(--card)', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{formatDeliveryDate(order.delivery_day)}</div>
          <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>{shortAddress(order.address)}</div>
          {order.brand && <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)', marginBottom: 2 }}>{order.brand}{order.volume ? ` ${order.volume}` : ''}</div>}
          <div style={{ fontSize: 14, marginBottom: 4 }}>Бутыли: {order.quantity}{order.exchange_count ? ` · Обмен: ${order.exchange_count}` : ''}</div>
          {order.comment && <div style={{ fontSize: 13, color: '#E53935', fontStyle: 'italic', marginBottom: 8 }}>{order.comment}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={e => handleTake(order.id, e)}
              disabled={!!takingId}
              style={{ flex: 1, padding: '10px', borderRadius: 10, background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: takingId ? 0.5 : 1 }}
            >
              {takingId === order.id ? '...' : 'Взять в работу'}
            </button>
            <button
              onClick={() => navigate(`/admin-order/${order.id}`)}
              style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'none', color: 'var(--text)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            >
              Посмотреть
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Tab({ active, onClick, label, count, sub }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 20, border: '1.5px solid',
      borderColor: active ? (sub ? '#5BB8F5' : 'var(--primary)') : 'var(--border)',
      background: active ? (sub ? '#5BB8F5' : 'var(--primary)') : 'var(--card)',
      color: active ? '#fff' : 'var(--text)', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {label}
      <span style={{ minWidth: 20, height: 20, borderRadius: 10, background: active ? 'rgba(255,255,255,0.3)' : 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: active ? '#fff' : 'var(--text)', padding: '0 4px' }}>
        {count}
      </span>
    </button>
  );
}

const centered = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh', fontSize: 16, color: 'var(--text-light)' };
